import React, { useState, useEffect } from 'react';
import { collection, getDocs, orderBy, query, doc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { toast } from 'react-toastify';
import Dashboard from './Dashboard';
import './admin.css';

const Contacts = () => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedContact, setSelectedContact] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchContacts();
  }, []);

  const fetchContacts = async () => {
    try {
      const q = query(collection(db, 'contacts'), orderBy('createdAt', 'desc'));
      const querySnapshot = await getDocs(q);
      const data = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setContacts(data);
    } catch (error) {
      console.error('Error fetching contacts:', error);
      toast.error('Failed to load contacts');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      await updateDoc(doc(db, 'contacts', id), {
        status: newStatus,
        statusUpdatedAt: new Date().toISOString()
      });
      setContacts(contacts.map(c => 
        c.id === id ? { ...c, status: newStatus } : c
      ));
      if (selectedContact?.id === id) {
        setSelectedContact({ ...selectedContact, status: newStatus });
      }
      toast.success(`Status updated to ${newStatus}`);
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Failed to update status');
    }
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'Seen': return '#3498db';
      case 'Action Taken': return '#f39c12';
      case 'Resolved': return '#27ae60';
      case 'Inactive': return '#95a5a6';
      case 'Disposed': return '#e74c3c';
      default: return '#7f8c8d';
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    try {
      let date;
      // Handle Firestore timestamp
      if (timestamp.seconds) {
        date = new Date(timestamp.seconds * 1000);
      }
      // Handle ISO string or regular date
      else if (typeof timestamp === 'string' || timestamp instanceof Date) {
        date = new Date(timestamp);
      } else {
        return 'N/A';
      }
      
      // Check if date is valid
      if (isNaN(date.getTime())) {
        return 'N/A';
      }
      
      return date.toLocaleString('en-IN', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return 'N/A';
    }
  };

  const filteredContacts = contacts.filter(contact => 
    contact.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    contact.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    contact.subject?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <Dashboard>
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading contacts...</p>
        </div>
      </Dashboard>
    );
  }

  return (
    <Dashboard>
      <div className="admin-page">
        <div className="page-header">
          <div className="header-info">
            <h2>Contact Messages</h2>
            <p>Total: {contacts.length} messages</p>
          </div>
          <div className="search-box">
            <svg className="search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="text"
              placeholder="Search by name, email, or subject..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        {filteredContacts.length === 0 ? (
          <div className="empty-state">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            <p>No contact messages found</p>
          </div>
        ) : (
          <div className="data-table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Subject</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {filteredContacts.map((contact) => (
                  <tr key={contact.id} onClick={() => setSelectedContact(contact)}>
                    <td>{formatDate(contact.createdAt)}</td>
                    <td>{contact.name}</td>
                    <td>{contact.email}</td>
                    <td>{contact.phone}</td>
                    <td>{contact.subject}</td>
                    <td>
                      <select
                        className="status-select"
                        value={contact.status || 'Pending'}
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) => {
                          e.stopPropagation();
                          handleStatusChange(contact.id, e.target.value);
                        }}
                        style={{ backgroundColor: getStatusColor(contact.status || 'Pending') }}
                      >
                        <option value="Pending">Pending</option>
                        <option value="Seen">Seen</option>
                        <option value="Action Taken">Action Taken</option>
                        <option value="Resolved">Resolved</option>
                        <option value="Inactive">Inactive</option>
                        <option value="Disposed">Disposed</option>
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Detail Modal */}
        {selectedContact && (
          <div className="modal-overlay" onClick={() => setSelectedContact(null)}>
            <div className="detail-modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3>Contact Details</h3>
                <button className="close-btn" onClick={() => setSelectedContact(null)}>×</button>
              </div>
              <div className="modal-body">
                <div className="detail-row">
                  <strong>Date:</strong>
                  <span>{formatDate(selectedContact.createdAt)}</span>
                </div>
                <div className="detail-row">
                  <strong>Name:</strong>
                  <span>{selectedContact.name}</span>
                </div>
                <div className="detail-row">
                  <strong>Email:</strong>
                  <span><a href={`mailto:${selectedContact.email}`}>{selectedContact.email}</a></span>
                </div>
                <div className="detail-row">
                  <strong>Phone:</strong>
                  <span><a href={`tel:${selectedContact.phone}`}>{selectedContact.phone}</a></span>
                </div>
                <div className="detail-row">
                  <strong>Subject:</strong>
                  <span>{selectedContact.subject}</span>
                </div>
                <div className="detail-row">
                  <strong>Status:</strong>
                  <select
                    className="status-select-modal"
                    value={selectedContact.status || 'Pending'}
                    onChange={(e) => handleStatusChange(selectedContact.id, e.target.value)}
                    style={{ backgroundColor: getStatusColor(selectedContact.status || 'Pending') }}
                  >
                    <option value="Pending">Pending</option>
                    <option value="Seen">Seen</option>
                    <option value="Action Taken">Action Taken</option>
                    <option value="Resolved">Resolved</option>
                    <option value="Inactive">Inactive</option>
                    <option value="Disposed">Disposed</option>
                  </select>
                </div>
                {selectedContact.message && (
                  <div className="detail-row message">
                    <strong>Message:</strong>
                    <p>{selectedContact.message}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </Dashboard>
  );
};

export default Contacts;
